/**
 * HTTP adapter for IGameRepository — syncs full save blob to platform API checkpoints.
 */
import type { GameSaveSnapshot, IGameRepository } from '../../domain/ports/IGameRepository';
import type { GameStatus } from '../../../types';
import { recoverSaveData, type SaveData } from '../../../utils/saveLoadValidator';
import { logCritical } from '../../../utils/errorHandler';
import { SAVE_VERSION } from '../../infrastructure/config/persistence';
import {
  PLATFORM_API_URL,
  SESSION_CHECKPOINT_FLAG_KEY,
  SESSION_STORAGE_KEY,
} from '../../infrastructure/config/platform';

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export type FetchLike = (
  input: RequestInfo | URL,
  init?: RequestInit,
) => Promise<Response>;

const defaultSessionStorage = (): StorageLike => {
  if (typeof sessionStorage !== 'undefined') {
    return sessionStorage;
  }
  throw new Error('sessionStorage is not available');
};

const defaultFetch = (): FetchLike => {
  if (typeof fetch !== 'function') {
    throw new Error('fetch is not available');
  }
  return fetch.bind(globalThis);
};

const toGameStatus = (value: unknown): GameStatus => {
  if (value === 'playing' || value === 'won' || value === 'lost' || value === 'scenario_won') {
    return value;
  }
  return 'playing';
};

const toSnapshot = (
  data: SaveData,
  raw: Record<string, unknown>,
  loadRecoveryReason?: string,
): GameSaveSnapshot => ({
  version: typeof raw.version === 'string' ? raw.version : data.version,
  timestamp: typeof raw.timestamp === 'number' ? raw.timestamp : data.timestamp,
  grid: data.grid,
  stats: data.stats,
  research: data.research,
  milestones: Array.isArray(raw.milestones) ? raw.milestones : [],
  gameStatus: toGameStatus(raw.gameStatus ?? data.gameStatus),
  newsFeed: Array.isArray(raw.newsFeed)
    ? raw.newsFeed
    : Array.isArray(data.newsFeed)
      ? data.newsFeed
      : [],
  currentGoal: (raw.currentGoal ?? data.currentGoal ?? null) as GameSaveSnapshot['currentGoal'],
  savedAt: typeof raw.savedAt === 'string' ? raw.savedAt : undefined,
  loadRecoveryReason,
});

const parseCheckpoint = (checkpoint: unknown): GameSaveSnapshot | null => {
  if (!checkpoint || typeof checkpoint !== 'object') {
    return null;
  }

  const parsed = checkpoint as Record<string, unknown>;
  const recovery = recoverSaveData(parsed);

  if (recovery.recovered) {
    logCritical('Corrupt remote checkpoint recovered during load', {
      source: 'HttpGameRepository',
      recoveryReason: recovery.recoveryReason,
      errors: recovery.validation.errors,
    });
  }

  return toSnapshot(
    recovery.data,
    { ...parsed, ...recovery.data },
    recovery.recoveryReason ?? undefined,
  );
};

interface SessionResponse {
  checkpoint?: unknown;
}

export class HttpGameRepository implements IGameRepository {
  constructor(
    private readonly apiBaseUrl: string = PLATFORM_API_URL,
    private readonly sessionStorageKey: string = SESSION_STORAGE_KEY,
    private readonly checkpointFlagKey: string = SESSION_CHECKPOINT_FLAG_KEY,
    private readonly storage: StorageLike = defaultSessionStorage(),
    private readonly fetchFn: FetchLike = defaultFetch(),
    private readonly sessionId?: string,
  ) {}

  private getSessionId(): string | null {
    return this.sessionId ?? this.storage.getItem(this.sessionStorageKey);
  }

  private markCheckpointPresent(): void {
    this.storage.setItem(this.checkpointFlagKey, 'true');
  }

  private clearCheckpointFlag(): void {
    this.storage.removeItem(this.checkpointFlagKey);
  }

  private sessionUrl(path: string): string {
    const sessionId = this.getSessionId();
    if (!sessionId) {
      throw new Error('No platform session id — create a session before saving');
    }
    return `${this.apiBaseUrl}/v1/sessions/${encodeURIComponent(sessionId)}${path}`;
  }

  async save(snapshot: GameSaveSnapshot): Promise<void> {
    const payload = {
      stats: snapshot.stats,
      research: snapshot.research,
      grid: snapshot.grid,
      milestones: snapshot.milestones,
      gameStatus: snapshot.gameStatus,
      newsFeed: snapshot.newsFeed,
      currentGoal: snapshot.currentGoal,
      version: snapshot.version ?? SAVE_VERSION,
      timestamp: snapshot.timestamp ?? Date.now(),
      savedAt: snapshot.savedAt ?? new Date().toISOString(),
    };

    const response = await this.fetchFn(this.sessionUrl('/checkpoint'), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`Failed to save checkpoint (${response.status})`);
    }

    this.markCheckpointPresent();
  }

  async load(): Promise<GameSaveSnapshot | null> {
    const sessionId = this.getSessionId();
    if (!sessionId) {
      return null;
    }

    try {
      const response = await this.fetchFn(this.sessionUrl(''));

      if (response.status === 404) {
        this.clearCheckpointFlag();
        return null;
      }

      if (!response.ok) {
        throw new Error(`Failed to load session (${response.status})`);
      }

      const body = (await response.json()) as SessionResponse;
      const snapshot = parseCheckpoint(body.checkpoint);

      if (snapshot) {
        this.markCheckpointPresent();
      } else {
        this.clearCheckpointFlag();
      }

      return snapshot;
    } catch {
      return null;
    }
  }

  hasSave(): boolean {
    const sessionId = this.getSessionId();
    if (!sessionId) {
      return false;
    }
    return this.storage.getItem(this.checkpointFlagKey) === 'true';
  }

  async delete(): Promise<void> {
    this.storage.removeItem(this.sessionStorageKey);
    this.clearCheckpointFlag();
  }
}